/**
 * Customer detail / bookings / patch hooks for the new ClientDetailPanel.
 *
 * Mirrors the booking-detail / service-detail data layer pattern: one
 * react-query cache per customer id, paginated bookings filter via the
 * `?customerId=` parameter, partial PATCH for inline edits.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./api";
import { authFetchJson } from "./auth-fetch";
import { useAuthStore } from "./auth";

/**
 * Tenant-wide custom-field value attached to a customer. Backend returns the
 * full schema (including fields the customer hasn't filled in yet — `value`
 * is null) so the panel can render empty inputs without a second roundtrip.
 */
export type CustomerCustomFieldValue = {
  fieldId: string;
  label: string;
  type: string;
  required: boolean;
  /** Schema-side options blob (SELECT only). Null for other types. */
  options?: unknown;
  value: string | null;
};

export type CustomerTagSummary = {
  id: string;
  name: string;
  color: string | null;
};

export type Customer = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  lifetimeValue: number;
  firstSeenAt: string;
  customFieldValues: CustomerCustomFieldValue[];
  tags: CustomerTagSummary[];
};

export function useCustomerDetail(id: string | null | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["customer", id],
    enabled: !!accessToken && !!id,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/customers/{id}", {
          params: { path: { id: id! } },
        }),
      ) as Customer,
  });
}

export function useCustomerBookings(customerId: string | null | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["bookings", "customer", customerId],
    enabled: !!accessToken && !!customerId,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/bookings", {
          params: { query: { customerId: customerId!, page: 0, size: 30 } },
        }),
      ),
  });
}

export type CreateCustomerInput = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  notes?: string;
  tagIds?: string[];
  customFieldValues?: { fieldId: string; value: string | null }[];
};

/**
 * Standalone customer creation. Backed by `POST /api/v1/customers` with
 * email-based upsert semantics — re-adding the same email returns the
 * existing row so the form is forgiving when an operator double-submits
 * or re-enters someone they forgot was already in the workspace.
 */
export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCustomerInput) =>
      authFetchJson<Customer>(`/api/v1/customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export type PatchCustomerInput = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  notes?: string;
  /** Upsert per row; blank values delete the existing row. */
  customFieldValues?: { fieldId: string; value: string | null }[];
  /** Replace the customer's tag-set. Null/undefined = leave untouched;
   *  pass an empty array to clear all tags. */
  tagIds?: string[];
};

export function usePatchCustomer(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchCustomerInput) =>
      authFetchJson<Customer>(`/api/v1/customers/${customerId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}
